import type { PartnerConfigurationService } from '../PartnerConfigurationService';
import type { PCOrigin, PCOriginScheduleEntry, PCOriginType, PCTenant } from '../../partnerConfiguration/types';

const MockedAddress1 = {
  street1: '111 Sesame St',
  street2: '#400',
  city: 'Seattle',
  company: 'Digital Finance Company',
  countryCode: 'America',
  postalCode: '98970',
  state: 'WA',
};

const MockedAddress2 = {
  street1: '222 TooToo St',
  street2: '#222',
  city: 'TuTu',
  company: 'GoodTwos',
  countryCode: 'America',
  postalCode: '12203',
  state: 'PA',
};

const MockedAddress3 = {
  street1: 'Narrow St',
  street2: '#999',
  city: 'Austin',
  company: 'Good Ribs Company',
  countryCode: 'America',
  postalCode: '97567',
  state: 'TX',
};

const MockedAddress4 = {
  street1: 'Wide St',
  street2: '#444',
  city: 'George Town',
  company: 'The Burger Shop',
  countryCode: 'America',
  postalCode: '19503',
  state: 'MO',
};

const MockedContact1 = {
  email: 'goofy@goober.com',
  name: 'Goofy',
  phoneNumber: '1234567890',
};

const MockedContact2 = {
  email: 'lucky@company.com',
  name: 'Unicorn',
  phoneNumber: '987654321',
};

const MockedContact3 = {
  email: 'man@man.com',
  name: 'Man',
  phoneNumber: '543219876',
};

const MockedContact4 = {
  email: 'breaking@bad.com',
  name: 'Walter',
  phoneNumber: '76543298',
};

const MockedOrigin1: PCOrigin = {
  partnerId: 'pid123',
  href: 'https://api.shipium.com/api/v1/partner-configuration/origins/origin1',
  originId: 'originid123',
  partnerProvidedId: 'partnerprovidedoriginid123',
  name: 'Very Good Origin',
  description: 'Very good origin :)',
  originType: 'fc' as PCOriginType,
  address: MockedAddress1,
  contact: MockedContact1,
  scheduleConfigured: true,
  orchestratedAssetMetadata: {
    orchestrationId: 'origin',
    orchestrationTimeStamp: new Date().toISOString(),
  },
  activeSchedule: {
    orchestratedAssetMetadata: {
      orchestrationId: 'schedule',
      orchestrationTimeStamp: new Date().toISOString(),
    },
    href: 'https://api.shipium.com/api/v1/partner-configuration/origins/origin1/schedules/schedule1',
    scheduleEntryId: 'schedule123',
    from: '2022-06-17T00:00:00.000Z',
    to: '2023-06-01T00:00:00.000Z',
    shippingDays: ['MONDAY', 'TUESDAY', 'WEDNESDAY'],
    processingCutoff: {
      expectedDaysToShip: 3,
      expectedDaysAndTimeToShip: 3,
      shippingCutoffHour: 17,
      weekendCutoffHour: 17,
    },
    holidays: [
      {
        date: '2021-01-21',
        metadata: {
          name: 'MLK DAY',
        },
      },
      {
        date: '2021-02-15',
        metadata: {
          name: 'Presidents Day',
        },
      },
      {
        date: '2021-05-05',
        metadata: {
          name: 'Cinco De Mayo',
        },
      },
    ],
  },
};

const MockedOriginTenant: PCOrigin = {
  partnerId: 'pidABC',
  href: 'https://api.shipium.com/api/v1/partner-configuration/origins/origin2',
  orchestratedAssetMetadata: {
    orchestrationId: 'originTen',
    orchestrationTimeStamp: new Date().toISOString(),
  },
  description: 'Very bad origin :)',
  originId: 'originidABC',
  partnerProvidedId: 'partnerprovidedoriginidABC',
  name: 'The Other Origin',
  originType: 'fc' as PCOriginType,
  address: MockedAddress2,
  contact: MockedContact2,
  scheduleConfigured: false,
  tenantLink: {
    tenantId: 'nicetenantid',
  },
};

const MockedSchedule1: PCOriginScheduleEntry = {
  href: 'https://api.shipium.com/api/v1/partner-configuration/origins/origin1/schedules/schedule1',
  scheduleEntryId: 'schedule123',
  from: '2022-06-17T00:00:00.000Z',
  to: '2023-06-01T00:00:00.000Z',
  orchestratedAssetMetadata: {
    orchestrationId: 'schedule1',
    orchestrationTimeStamp: new Date().toISOString(),
  },
  shippingDays: ['MONDAY', 'TUESDAY', 'WEDNESDAY'],
  processingCutoff: {
    expectedDaysToShip: 3,
    expectedDaysAndTimeToShip: 3,
    shippingCutoffHour: 17,
    weekendCutoffHour: 17,
  },
  holidays: [
    {
      date: '2021-01-21',
      metadata: {
        name: 'MLK DAY',
      },
    },
    {
      date: '2021-02-15',
      metadata: {
        name: 'Presidents Day',
      },
    },
    {
      date: '2021-05-05',
      metadata: {
        name: 'Cinco De Mayo',
      },
    },
  ],
};

const MockedSchedule2: PCOriginScheduleEntry = {
  href: 'https://api.shipium.com/api/v1/partner-configuration/origins/origin1/schedules/schedule2',
  scheduleEntryId: 'scheduleabc',
  from: '2021-08-17T00:00:00.000Z',
  to: '2022-05-01T00:00:00.000Z',
  orchestratedAssetMetadata: {
    orchestrationId: 'schedule2',
    orchestrationTimeStamp: new Date().toISOString(),
  },
  shippingDays: ['THURSDAY', 'FRIDAY'],
  processingCutoff: {
    expectedDaysToShip: 2,
    expectedDaysAndTimeToShip: 2,
    shippingCutoffHour: 15,
    weekendCutoffHour: 15,
  },
  holidays: [],
};

const MockedTenant1 = {
  partnerProvidedId: 'bigthreepl',
  tenantId: 'nicetenantid',
  taxIdentificationNumber: 'nicetaxidnumber',
  name: 'Nice Tenant',
  unitedStatesPrincipalPartyInInterestContact: MockedContact3,
  unitedStatesPrincipalPartyInInterestAddress: MockedAddress3,
} as PCTenant;

const MockedTenant2 = {
  partnerProvidedId: 'bigthreepl',
  tenantId: 'notnicetenantid',
  taxIdentificationNumber: 'notnicetaxidnumber',
  name: 'Not Nice Tenant',
  unitedStatesPrincipalPartyInInterestContact: MockedContact4,
  unitedStatesPrincipalPartyInInterestAddress: MockedAddress4,
} as PCTenant;

export const mockPartnerConfigurationService: PartnerConfigurationService = {
  fetchPCOrigins: async () => {
    return {
      orchestratedAssetMetadata: {
        orchestrationId: 'orca',
        orchestrationTimeStamp: new Date().toISOString(),
      },
      results: [MockedOrigin1, MockedOriginTenant],
    };
  },
  fetchPCOrigin: async (payload) => {
    return [MockedOrigin1, MockedOriginTenant].find((val) => val.originId === payload) || MockedOrigin1;
  },
  createPCOrigin: async (payload) => {
    return { ...MockedOrigin1, ...payload };
  },
  updatePCOrigin: async (payload) => {
    return { ...MockedOrigin1, ...payload };
  },
  deletePCOrigin: async (payload) => {
    return MockedOrigin1;
  },
  fetchPCSchedules: async (payload) => {
    return {
      orchestratedAssetMetadata: {
        orchestrationId: 'schedresidk',
        orchestrationTimeStamp: new Date().toISOString(),
      },
      results: [MockedSchedule1, MockedSchedule2],
    };
  },
  createPCSchedule: async (originId, payload) => {
    return MockedSchedule1;
  },
  deletePCSchedule: async (originId, scheduleId) => {
    return MockedSchedule1;
  },
  fetchPCTenants: async () => {
    return {
      orchestratedAssetMetadata: {
        orchestrationId: 'tenny',
        orchestrationTimeStamp: new Date().toISOString(),
      },
      results: [MockedTenant1, MockedTenant2],
    };
  },
  fetchPCTenant: async (payload) => {
    return [MockedTenant1, MockedTenant2].find((val) => val.tenantId === payload) || MockedTenant1;
  },
  createPCTenant: async (payload) => {
    return { ...MockedTenant1, ...payload };
  },
  updatePCTenant: async (payload) => {
    return { ...MockedTenant1, ...payload };
  },
  deletePCTenant: async (payload) => {
    return MockedTenant1;
  },
};
